import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UserLayout from '../../components/user/UserLayout';
import { CreditCard, ExternalLink, QrCode, Radio } from 'lucide-react';
import { apiRequest } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function UserCards() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchCards();
  }, [user, navigate]);

  const fetchCards = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await apiRequest(`${BACKEND_URL}/api/user/cards`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setCards(response.data.cards || []);
      } else {
        setError('Failed to fetch cards');
      }
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <UserLayout>
        <div className="p-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading cards...</p>
          </div>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-extrabold text-navy">My Cards</h1>
          <p className="text-gray-600 mt-2">View and manage your delivered cards</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {cards.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cards.map((card) => (
              <div key={card.transaction_id} className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow">
                {/* Card Header */}
                <div className="bg-gradient-to-br from-navy to-purple-600 p-6 text-white">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold">{card.order_data?.displayName || 'Business Card'}</h3>
                      <p className="text-sm opacity-90 mt-1">{card.order_data?.cardType || 'Digital Card'}</p>
                    </div>
                    <CreditCard className="w-8 h-8 opacity-80" />
                  </div>
                  <div className="flex items-center gap-2 text-sm opacity-90">
                    <span className="px-2 py-1 bg-white/20 rounded">
                      {card.order_data?.selectedMaterial?.name || 'N/A'}
                    </span>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-6">
                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Order ID:</span>
                      <span className="font-mono font-semibold">#{card.transaction_id?.slice(0, 10)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Delivered:</span>
                      <span className="font-semibold text-green-600">
                        {new Date(card.updated_at || card.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {/* Card Tracking Stats */}
                  {card.dynamic_link_details && (
                    <div className="border-t border-gray-200 pt-4 space-y-3">
                      <p className="text-xs font-semibold text-gray-600 uppercase">Card Statistics</p>

                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="flex items-center gap-1 p-2 bg-green-50 rounded">
                          <QrCode className="w-3 h-3 text-green-600" />
                          <span className="text-green-700">QR: {card.dynamic_link_details.qr_scans || 0}</span>
                        </div>
                        <div className="flex items-center gap-1 p-2 bg-purple-50 rounded">
                          <Radio className="w-3 h-3 text-purple-600" />
                          <span className="text-purple-700">NFC: {card.dynamic_link_details.nfc_taps || 0}</span>
                        </div>
                        <div className="flex items-center gap-1 p-2 bg-blue-50 rounded">
                          <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                          <span className="text-blue-700">Direct: {card.dynamic_link_details.direct_opens || 0}</span>
                        </div>
                      </div>

                      {card.dynamic_link_details.redirect_url && (
                        <div className="text-xs text-gray-600">
                          <p className="mb-1">Redirects to:</p>
                          <a
                            href={card.dynamic_link_details.redirect_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline truncate block"
                          >
                            {card.dynamic_link_details.redirect_url}
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  {!card.dynamic_link_details && (
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-xs text-gray-500 text-center">Card will be activated once delivered</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-navy mb-2">No Cards Yet</h3>
            <p className="text-gray-600 mb-6">
              Your delivered cards will appear here. Start by placing an order!
            </p>
            <button
              onClick={() => navigate('/review-cards')}
              className="px-6 py-3 bg-navy text-white rounded-lg hover:bg-navy/90 transition-colors"
            >
              Browse Cards
            </button>
          </div>
        )}
      </div>
    </UserLayout>
  );
}
